
import React, { useState, useEffect, useRef } from 'react';
import { RoomData, Player, Wing } from '../types';
import Avatar from './Avatar';
import { isStaff } from '../utils/roles';

interface Hallway3DProps {
  rooms: RoomData[];
  wings: Wing[];
  currentWingId: string;
  me: Player;
  others: Player[];
  onFloorClick: (e: React.MouseEvent) => void;
  onDoorClick: (roomId: string) => void;
  checkAccess: (roomId: string) => { hasAccess: boolean; reason?: string };
  onBackToHub: () => void;
  hubBackgroundUrl?: string;
  onUpdateHub?: (url: string) => void;
  onPlayerClick?: (player: Player) => void;
  scheduleCard?: React.ReactNode;
  viewer?: Player;
}

const s = (val: any): string => {
  if (typeof val === 'string') return val;
  if (!val) return '';
  if (typeof val === 'object') {
    return val.da || val.en || val.nb || Object.values(val).find(v => typeof v === 'string') || "";
  }
  return String(val);
};

const Hallway3D: React.FC<Hallway3DProps> = ({ rooms, wings, currentWingId, me, others, onFloorClick, onDoorClick, onBackToHub, onPlayerClick, checkAccess, hubBackgroundUrl, onUpdateHub, scheduleCard, viewer }) => {
  const isHub = currentWingId === 'hub';
  const isStaffMember = isStaff((viewer || me).role);
  const lockMode = (item: any): string => ((item && item.lockedDisplay) || 'lock');
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const move = direction === 'left' ? -clientWidth / 1.5 : clientWidth / 1.5;
      scrollRef.current.scrollTo({ left: scrollLeft + move, behavior: 'smooth' });
    }
  };

  const visibleWings = wings.filter(w => isStaffMember || (w.isActive !== false && (lockMode(w) !== 'hide' || checkAccess(w.id).hasAccess))).sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
  const currentWing = wings.find(w => w.id === currentWingId);
  const wingRooms = rooms.filter(r => r.wingIds?.includes(currentWingId) && (isStaffMember || (r.isActive !== false && (lockMode(r) !== 'hide' || checkAccess(r.id).hasAccess)))).sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
  
  const backgroundUrl = isHub 
    ? (hubBackgroundUrl || 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?q=80&w=2000') 
    : (currentWing?.backgroundUrl || currentWing?.themeUrl || 'https://images.unsplash.com/photo-1514373941175-0a1410229b4c?q=80&w=2000');

  return (
    <div className="relative w-full h-full bg-[#050510] overflow-hidden" onClick={onFloorClick}>
      <div className="absolute inset-0 z-0">
        <img src={backgroundUrl} className={"absolute inset-0 w-full h-full object-cover object-center transition-all duration-1000"} style={{ opacity: 0.35 }} alt="" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05050a] via-transparent to-[#05050a]" />
      </div>

      {scheduleCard}
      {!isHub && (
        <button onClick={(e) => { e.stopPropagation(); onBackToHub(); }} className="absolute top-12 left-1/2 -translate-x-1/2 z-[60] bg-[#0a0a1a]/90 border border-yellow-500/30 px-6 md:px-8 py-2 md:py-3 rounded-full font-cinzel text-[8px] md:text-[10px] tracking-[0.3em] text-yellow-500 hover:bg-yellow-500 hover:text-indigo-950 transition-all shadow-2xl backdrop-blur-md pointer-events-auto">
          🏰 TILBAGE
        </button>
      )}

      {isHub && visibleWings.length > 1 && (
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-[70] flex justify-between px-4 md:px-8 pointer-events-none">
          <button onClick={(e) => { e.stopPropagation(); scroll('left'); }} className="w-12 h-12 md:w-16 md:h-16 bg-black/40 border-2 border-yellow-500/20 rounded-full flex items-center justify-center text-yellow-500 hover:bg-yellow-500 hover:text-black transition-all shadow-2xl pointer-events-auto">
             <span className="text-2xl md:text-3xl">‹</span>
          </button>
          <button onClick={(e) => { e.stopPropagation(); scroll('right'); }} className="w-12 h-12 md:w-16 md:h-16 bg-black/40 border-2 border-yellow-500/20 rounded-full flex items-center justify-center text-yellow-500 hover:bg-yellow-500 hover:text-black transition-all shadow-2xl pointer-events-auto">
             <span className="text-2xl md:text-3xl">›</span>
          </button>
        </div>
      )}

      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none overflow-hidden">
        {isHub ? (
          <div ref={scrollRef} className="w-full flex gap-6 md:gap-12 px-[10vw] md:px-[25vw] py-20 pointer-events-auto items-center overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth">
            {visibleWings.map((wing) => {
              const access = checkAccess(wing.id);
              // Clean up: Wing interface now includes description property
              const description = s(wing.description);
              return (
                <div key={wing.id} className="group relative cursor-pointer flex-shrink-0 transition-all hover:scale-105 active:scale-95 snap-center" onClick={(e) => { e.stopPropagation(); onDoorClick(wing.id); }}>
                    {description && (
                      <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-56 opacity-0 group-hover:opacity-100 transition-all duration-500 z-[100] pointer-events-none">
                        <div className="bg-indigo-950/90 border border-yellow-500/30 p-3 rounded-xl shadow-2xl backdrop-blur-md">
                        <p className="text-xs text-yellow-100 font-medium italic text-center line-clamp-3 leading-relaxed">"{description}"</p>
                        </div>
                        <div className="w-2 h-2 bg-indigo-950 border-r border-b border-yellow-500/30 rotate-45 mx-auto -mt-1" />
                      </div>
                    )}
                    <div className={`w-56 h-[22rem] md:w-64 md:h-[28rem] lg:w-72 lg:h-[32rem] xl:w-80 xl:h-[36rem] bg-black/80 border-2 ${!access.hasAccess ? (lockMode(wing) === 'dim' ? 'border-white/10 grayscale opacity-25' : 'border-red-900 grayscale opacity-60') : (wing.isActive === false ? 'border-red-500' : 'border-yellow-500/20')} rounded-t-full relative overflow-hidden group-hover:border-yellow-500 transition-all duration-500 shadow-2xl`}>
                      {wing.themeUrl && <img src={wing.themeUrl} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-80 transition-all duration-700" alt={s(wing.name)} />}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                      {!access.hasAccess && (
                        <div className="absolute inset-0 flex items-center justify-center">
                           <span className="text-4xl filter drop-shadow-[0_0_10px_rgba(0,0,0,1)]">🔒</span>
                        </div>
                      )}
                      {!access.hasAccess && access.reason && (
                        <div className="absolute inset-x-0 top-1/2 mt-10 px-3 text-center pointer-events-none">
                          <p className="text-[9px] text-white/75 font-bold leading-snug drop-shadow-[0_0_6px_rgba(0,0,0,1)]">{access.reason}</p>
                        </div>
                      )}
                      <div className="absolute inset-0 flex flex-col items-center justify-end pb-10 md:pb-12 p-4 md:p-6 text-center">
                          <h3 className="text-base md:text-lg font-cinzel text-yellow-500 uppercase tracking-widest leading-tight drop-shadow-lg">{s(wing.name)}</h3>
                      </div>
                    </div>
                </div>
              );
            })}
            <div className="w-[10vw] md:w-[25vw] flex-shrink-0" />
          </div>
        ) : (
          <div className="w-full h-full relative pointer-events-auto flex flex-wrap justify-center items-start content-start gap-6 md:gap-12 overflow-y-auto no-scrollbar pt-44 md:pt-56 px-10 md:px-24 pb-48">
            {wingRooms.map((room) => {
              const access = checkAccess(room.id);
              const description = s(room.description);
              return (
                <div key={room.id} className="group cursor-pointer transition-all hover:scale-105 active:scale-95 relative" onClick={(e) => { e.stopPropagation(); onDoorClick(room.id); }}>
                  {description && (
                    <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-48 opacity-0 group-hover:opacity-100 transition-all duration-500 z-[100] pointer-events-none">
                      <div className="bg-indigo-950/90 border border-indigo-400/30 p-2 rounded-lg shadow-2xl backdrop-blur-md">
                      <p className="text-[10px] text-indigo-100 font-medium italic text-center line-clamp-2 leading-relaxed">"{description}"</p>
                      </div>
                      <div className="w-2 h-2 bg-indigo-950 border-r border-b border-indigo-400/30 rotate-45 mx-auto -mt-1" />
                    </div>
                  )}
                  <div className={`w-40 h-56 md:w-52 md:h-72 lg:w-60 lg:h-[22rem] xl:w-64 xl:h-96 bg-indigo-950/50 border-2 ${!access.hasAccess ? (lockMode(room) === 'dim' ? 'border-white/10 grayscale opacity-25' : 'border-red-900 grayscale opacity-40') : (room.isActive === false ? 'border-red-500/60' : 'border-indigo-400/20')} rounded-t-full relative overflow-hidden group-hover:border-yellow-500 transition-all duration-500 shadow-2xl`}>
                    {room.imageUrl && <img src={room.imageUrl} className="w-full h-full object-cover opacity-30 group-hover:opacity-60 transition-all duration-700" alt={s(room.name)} />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    {!access.hasAccess && (
                      <div className="absolute inset-0 flex items-center justify-center">
                         <span className="text-3xl opacity-60">🔒</span>
                      </div>
                    )}
                    {!access.hasAccess && access.reason && (
                      <div className="absolute inset-x-0 top-1/2 mt-10 px-3 text-center pointer-events-none">
                        <p className="text-[9px] text-white/75 font-bold leading-snug drop-shadow-[0_0_6px_rgba(0,0,0,1)]">{access.reason}</p>
                      </div>
                    )}
                    <div className="absolute inset-0 flex flex-col items-center justify-end pb-8 md:pb-10 p-4 text-center">
                       <span className="text-[10px] md:text-[12px] font-cinzel text-indigo-100 group-hover:text-yellow-500 uppercase tracking-widest drop-shadow-md">{s(room.name)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="absolute inset-0 z-20 pointer-events-none">
        <Avatar3D player={me} isMe={true} onPlayerClick={onPlayerClick} />
        {others.filter(o => o.currentRoom === (isHub ? 'HALLWAY' : currentWingId)).map(p => (
          <Avatar3D key={p.id} player={p} isMe={false} onPlayerClick={onPlayerClick} />
        ))}
      </div>
    </div>
  );
};

const Avatar3D: React.FC<{ player: Player, isMe: boolean, onPlayerClick?: (p: Player) => void }> = ({ player, isMe, onPlayerClick }) => {
  return (
    <div className="absolute transition-all duration-700 ease-out" style={{ left: `${player.x}%`, top: `${player.y}%`, transform: `translate(-50%, -100%) scale(${0.6 + (player.y / 150)})`, zIndex: Math.floor(player.y) }}>
      <Avatar player={player} isMe={isMe} onClick={() => onPlayerClick?.(player)} />
    </div>
  );
};

export default Hallway3D;
